import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, LogOut, ChevronLeft, ChevronRight, List, Columns2, Ban, Clock, Sparkles, User as UserIcon, Bell, StickyNote } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatTime12 } from "@/lib/i18n";

const DAY_MIN_START = 9 * 60;       // 9:00 AM
const DAY_MIN_END = 19 * 60;        // 7:00 PM
const PIXELS_PER_MIN = 1.6;         // mobile-friendly: more compact than admin

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function minToHHMM(m) { return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; }

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [staffList, setStaffList] = useState([]); // self + linked
  const [view, setView] = useState("grid"); // grid | list
  const [blockOpen, setBlockOpen] = useState(false);
  // Tap any booking card to open a read-only detail dialog. Staff are NOT
  // allowed to check-in customers themselves (per owner policy), and they
  // must NOT see the customer's phone number. The detail dialog shows only
  // name, services, and start/end time so the tech can read the small grid
  // cards more clearly.
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const dStr = fmtDate(date);
    try {
      const { data } = await api.get("/staff/my-schedule", { params: { date_from: dStr, date_to: dStr } });
      setBookings(data.bookings || []);
      setStaffList(data.staff || [{ id: user?.id, name: user?.full_name }]);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const onLogout = async () => { await logout(); nav("/login"); };
  const onBlockSaved = () => { setBlockOpen(false); load(); };
  const move = (n) => { const x = new Date(date); x.setDate(x.getDate() + n); setDate(x); };

  const sorted = [...bookings].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const totalMin = DAY_MIN_END - DAY_MIN_START;
  const hours = []; for (let m = DAY_MIN_START; m <= DAY_MIN_END; m += 60) hours.push(m);

  return (
    <div className="min-h-screen bg-zen-bg">
      <header className="bg-white border-b border-zen-border px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zen-muted">Staff</div>
          <div className="font-serif text-lg sm:text-xl text-zen-ink truncate">
            {staffList.length > 1 ? staffList.map((s) => s.name).join(" & ") : (user?.full_name || "")}
          </div>
        </div>
        <Button onClick={onLogout} variant="outline" size="sm" className="rounded-full" data-testid="staff-logout-btn">
          <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Sign Out</span>
        </Button>
      </header>

      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Button size="icon" variant="ghost" onClick={() => move(-1)} data-testid="staff-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-serif text-base sm:text-lg text-zen-ink flex-1 text-center">{date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
          <Button size="icon" variant="ghost" onClick={() => move(1)} data-testid="staff-next-day"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setBlockOpen(true)}
            className="rounded-full border-zen-ink/50 text-zen-ink hover:bg-zen-ink hover:text-white"
            data-testid="staff-block-time-btn">
            <Ban className="w-3 h-3 mr-1" /> Block
          </Button>
          <div className="inline-flex rounded-full border border-zen-border p-0.5 bg-white ml-1">
            <button onClick={() => setView("grid")} className={`px-2 h-8 rounded-full text-xs ${view === "grid" ? "bg-zen-primary text-white" : "text-zen-muted"}`} data-testid="staff-view-grid"><Columns2 className="w-4 h-4" /></button>
            <button onClick={() => setView("list")} className={`px-2 h-8 rounded-full text-xs ${view === "list" ? "bg-zen-primary text-white" : "text-zen-muted"}`} data-testid="staff-view-list"><List className="w-4 h-4" /></button>
          </div>
        </div>

        {sorted.length === 0 && view === "list" && (
          <div className="text-center py-12 text-zen-muted"><CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" /> No appointments</div>
        )}

        {view === "list" && (
          <div className="space-y-3">
            {sorted.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setDetail(b)}
                className={`w-full text-left rounded-2xl border p-4 transition ${b.is_block_time ? "bg-zen-ink/10 hover:bg-zen-ink/20" : b.checked_in ? "bg-zen-cal-gray/20 hover:bg-zen-cal-gray/30" : b.requested_any ? "bg-zen-cal-green/20 hover:bg-zen-cal-green/30" : "bg-white hover:bg-zen-surface"} border-zen-border`}
                data-testid={`staff-booking-${b.id}`}>
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="font-bold text-zen-ink truncate text-base">{b.is_block_time ? "🚫 Blocked" : b.customer_name}</div>
                      {!b.is_block_time && (b.customer_notes || "").trim() && (
                        <span className="bg-amber-300 text-amber-900 px-1 rounded-full leading-none py-0.5 shrink-0"
                          title="Customer has a note — tap to read"
                          data-testid={`staff-note-bell-${b.id}`}>
                          <Bell className="w-3 h-3" strokeWidth={2.5} fill="currentColor" />
                        </span>
                      )}
                      {!b.is_block_time && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 rounded-full py-[2px] leading-none"
                          style={{
                            background: b.requested_any ? "#1B5E3C" : "#B22222",
                            color: "white",
                          }}>
                          {b.requested_any ? "ASSIGN" : "REQUEST"}
                        </span>
                      )}
                      {b.checked_in && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 rounded-full py-[2px] leading-none bg-zen-muted/30 text-zen-ink">
                          CHECKED-IN
                        </span>
                      )}
                    </div>
                    {staffList.length > 1 && b.staff_name && <div className="text-[11px] text-zen-muted mt-0.5">{b.staff_name}</div>}
                    <div className="text-sm font-medium text-zen-ink mt-1 truncate">{b.is_block_time ? (b.block_reason || "") : b.services_summary}</div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <div className="font-mono text-zen-ink font-semibold text-sm">{formatTime12(b.start_time)}</div>
                    <div className="text-[11px] text-zen-muted">{b.duration_minutes} min</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {view === "grid" && (
          <div className="bg-white rounded-2xl border border-zen-border overflow-hidden" data-testid="staff-grid-view">
            <div className="grid" style={{ gridTemplateColumns: `48px repeat(${staffList.length}, 1fr)` }}>
              <div className="border-r border-b border-zen-border bg-zen-surface/50"></div>
              {staffList.map((s) => (
                <div key={s.id} className="border-r border-b border-zen-border p-2 text-xs font-semibold text-zen-ink bg-zen-surface/50 truncate text-center">
                  {s.name}
                </div>
              ))}

              <div className="border-r border-zen-border relative bg-white" style={{ height: totalMin * PIXELS_PER_MIN }}>
                {hours.map((h) => (
                  <div key={h} className="absolute left-0 right-0 border-t-2 border-zen-border text-[9px] font-medium text-zen-ink pl-1"
                    style={{ top: (h - DAY_MIN_START) * PIXELS_PER_MIN }}>
                    {formatTime12(minToHHMM(h))}
                  </div>
                ))}
              </div>

              {staffList.map((s) => (
                <div key={s.id} className="border-r border-zen-border relative" style={{ height: totalMin * PIXELS_PER_MIN }}
                  data-testid={`staff-col-${s.id}`}>
                  {hours.map((h) => (
                    <div key={h} className="absolute left-0 right-0 border-t-2 border-zen-border/40"
                      style={{ top: (h - DAY_MIN_START) * PIXELS_PER_MIN, height: 60 * PIXELS_PER_MIN }} />
                  ))}
                  {bookings.filter((b) => b.staff_id === s.id).map((b) => {
                    const start = parseInt(b.start_time.split(":")[0]) * 60 + parseInt(b.start_time.split(":")[1]);
                    const top = Math.max(0, (start - DAY_MIN_START) * PIXELS_PER_MIN);
                    const h = Math.max(20, b.duration_minutes * PIXELS_PER_MIN);
                    // Card background uses gray for checked-in customers so
                    // the tech can spot who's already arrived, but we KEEP an
                    // explicit Request/Assign indicator (border + label) on
                    // top — per owner: tech must still know whether the
                    // customer specifically requested this stylist.
                    const bgCls = b.is_block_time ? "bg-zen-ink/80 text-white" :
                                  b.checked_in ? "bg-zen-cal-gray text-white" :
                                  b.requested_any ? "bg-zen-cal-green text-white" :
                                  "bg-zen-cal-red text-white";
                    const ringCls = !b.is_block_time
                      ? (b.requested_any ? "ring-2 ring-emerald-700" : "ring-2 ring-red-800")
                      : "";
                    return (
                      <button key={b.id} onClick={() => setDetail(b)}
                        className={`absolute left-0.5 right-0.5 rounded-md px-1.5 py-1 text-left ${bgCls} ${ringCls} z-10 overflow-hidden`}
                        style={{ top, height: h }} data-testid={`staff-grid-booking-${b.id}`}>
                        <div className="font-bold text-[11px] leading-tight truncate flex items-center gap-0.5">
                          {!b.is_block_time && (b.customer_notes || "").trim() && (
                            <Bell className="w-2.5 h-2.5 text-amber-300 shrink-0" strokeWidth={2.5} fill="currentColor" />
                          )}
                          <span className="truncate">{b.is_block_time ? "🚫" : b.customer_name}</span>
                        </div>
                        <div className="text-[10px] leading-tight truncate opacity-95">{b.is_block_time ? (b.block_reason || "Block") : b.services_summary}</div>
                        <div className="flex items-center justify-between gap-1 mt-0.5">
                          <div className="text-[9px] opacity-80">{formatTime12(b.start_time)}</div>
                          {!b.is_block_time && (
                            <span className="text-[8px] font-bold uppercase tracking-wider bg-white/95 px-1 rounded leading-none py-[1px]"
                              style={{ color: b.requested_any ? "#0F4A2F" : "#7A1818" }}>
                              {b.requested_any ? "ASSIGN" : "REQUEST"}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            {bookings.length === 0 && (
              <div className="p-8 text-center text-zen-muted text-sm"><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-40" /> No appointments today</div>
            )}
          </div>
        )}
      </main>
      <StaffBlockDialog
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        onSaved={onBlockSaved}
        staffList={staffList}
        defaultDate={date}
      />
      <StaffBookingDetailDialog
        booking={detail}
        onClose={() => setDetail(null)}
        staffList={staffList}
        onBlockRemoved={() => { setDetail(null); load(); }} />
    </div>
  );
}

// Read-only booking detail dialog for staff. Per owner policy: staff CAN see
// name, services, time, duration, status — but NEVER the customer's phone
// number, and they CANNOT change the booking (no check-in / no-show buttons).
// Reception or owner handles all status changes. EXCEPTION: a staff member
// CAN delete a block-time they (or owner) made for themselves — owner
// request Jun 10: "thợ có thể xoá block hẹn được".
function StaffBookingDetailDialog({ booking, onClose, staffList, onBlockRemoved }) {
  if (!booking) return null;
  const startMin = parseInt(booking.start_time.split(":")[0]) * 60 + parseInt(booking.start_time.split(":")[1]);
  const endMin = startMin + (booking.duration_minutes || 0);
  const endTime = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
  const staffName = booking.staff_name
    || staffList.find((s) => s.id === booking.staff_id)?.name
    || "—";
  const status = booking.is_block_time
    ? { label: "BLOCKED", color: "bg-zen-ink/15 text-zen-ink" }
    : booking.checked_in ? { label: "CHECKED-IN", color: "bg-emerald-100 text-emerald-800" }
              : booking.status === "no_show" ? { label: "NO-SHOW", color: "bg-red-100 text-red-700" }
              : booking.status === "confirmed" ? { label: "CONFIRMED", color: "bg-blue-100 text-blue-800" }
              : !booking.requested_any ? { label: "REQUEST", color: "bg-amber-100 text-amber-800" }
              : { label: "BOOKED", color: "bg-zen-surface text-zen-ink" };
  const handleRemoveBlock = async () => {
    if (!window.confirm("Remove this block?")) return;
    try {
      await api.delete(`/staff/block-time/${booking.id}`);
      toast.success("Block removed");
      onBlockRemoved && onBlockRemoved();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to remove");
    }
  };
  // Always-visible Assign/Request indicator — separate from the live status
  // so the tech sees BOTH "CHECKED-IN" AND whether the customer originally
  // requested THIS specific stylist.
  const intent = booking.is_block_time
    ? null
    : (booking.requested_any
        ? { label: "ASSIGN", color: "bg-emerald-700 text-white" }
        : { label: "REQUEST", color: "bg-red-700 text-white" });
  return (
    <Dialog open={!!booking} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="staff-detail-dialog">
        <DialogTitle className="sr-only">Appointment Details</DialogTitle>
        <div className="bg-zen-surface px-5 py-4 border-b border-zen-border flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zen-muted font-semibold">Appointment</div>
            <div className="font-serif text-xl text-zen-ink mt-1 leading-tight">
              {formatTime12(booking.start_time)} – {formatTime12(endTime)}
            </div>
            <div className="text-[11px] text-zen-muted mt-0.5">{booking.duration_minutes} min</div>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0 ${status.color}`}>
            {status.label}
          </span>
        </div>
        {intent && (
          <div className="px-5 pt-3">
            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${intent.color}`}
              data-testid="staff-detail-intent">
              {intent.label === "ASSIGN" ? "Assigned to you" : "Customer requested you"}
            </span>
          </div>
        )}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-start gap-3" data-testid="staff-detail-name">
            <div className="w-8 h-8 rounded-full bg-zen-surface flex items-center justify-center flex-shrink-0 mt-0.5">
              <UserIcon className="w-4 h-4 text-zen-muted" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zen-muted font-semibold">Customer</div>
              <div className="text-base text-zen-ink font-semibold leading-snug break-words">{booking.customer_name || "—"}</div>
            </div>
          </div>
          <div className="flex items-start gap-3" data-testid="staff-detail-services">
            <div className="w-8 h-8 rounded-full bg-zen-surface flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-zen-muted" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zen-muted font-semibold">Services</div>
              <div className="text-sm text-zen-ink font-medium leading-snug break-words">{booking.services_summary || "—"}</div>
            </div>
          </div>
          {staffList.length > 1 && (
            <div className="flex items-start gap-3" data-testid="staff-detail-tech">
              <div className="w-8 h-8 rounded-full bg-zen-surface flex items-center justify-center flex-shrink-0 mt-0.5">
                <Clock className="w-4 h-4 text-zen-muted" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-zen-muted font-semibold">With</div>
                <div className="text-sm text-zen-ink font-medium">{staffName}</div>
              </div>
            </div>
          )}
          {(booking.customer_notes || "").trim() && (
            <div className="flex items-start gap-3" data-testid="staff-detail-notes">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5 ring-1 ring-amber-300">
                <StickyNote className="w-4 h-4 text-amber-700" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-amber-700 font-bold">Note from customer</div>
                <div className="text-sm text-zen-ink font-medium leading-snug break-words whitespace-pre-wrap mt-0.5">
                  {booking.customer_notes}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 bg-zen-bg border-t border-zen-border flex justify-between items-center gap-2">
          {booking.is_block_time ? (
            <Button size="sm" variant="outline" onClick={handleRemoveBlock}
              className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="staff-detail-remove-block">
              Remove block
            </Button>
          ) : <span />}
          <Button size="sm" variant="ghost" onClick={onClose} className="rounded-full" data-testid="staff-detail-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaffBlockDialog({ open, onClose, onSaved, staffList, defaultDate }) {
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("13:00");
  const [endTime, setEndTime] = useState("14:00");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStaffId(staffList[0]?.id || "");
      setDate(fmtDate(defaultDate));
      setStartTime("13:00");
      setEndTime("14:00");
      setReason("");
    }
  }, [open, staffList, defaultDate]);

  const onSave = async () => {
    if (!staffId || !date || !startTime || !endTime) {
      toast.error("Fill all fields"); return;
    }
    if (startTime >= endTime) {
      toast.error("End time must be after start time"); return;
    }
    setBusy(true);
    try {
      await api.post("/staff/block-time", {
        staff_id: staffId,
        dates: [date],
        start_time: startTime,
        end_time: endTime,
        reason: reason.trim(),
      });
      toast.success("Time blocked — you're off then");
      onSaved();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Block failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="staff-block-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Ban className="w-5 h-5 text-zen-ink" /> Block My Time
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-zen-muted">
            Blocking time means customers can&apos;t book you for that slot. Great for leaving early, lunch, or a personal appointment.
          </div>
          {staffList.length > 1 && (
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger className="bg-zen-surface border-0 h-11" data-testid="staff-block-whom">
                <SelectValue placeholder="Who?" />
              </SelectTrigger>
              <SelectContent>
                {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">Date</div>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="bg-zen-surface border-0 h-12 text-base" data-testid="staff-block-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">From</div>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                step="900"
                className="bg-zen-surface border-0 h-12 text-base" data-testid="staff-block-start" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1 font-semibold">Until</div>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                step="900"
                className="bg-zen-surface border-0 h-12 text-base" data-testid="staff-block-end" />
            </div>
          </div>
          <Input placeholder="Reason (optional, e.g. Leaving early, Lunch)"
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="bg-zen-surface border-0 h-11" data-testid="staff-block-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={onSave} disabled={busy}
            className="rounded-full bg-zen-ink hover:bg-zen-ink/90 text-white px-6"
            data-testid="staff-block-save">
            {busy ? "Saving…" : "Block Time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
